(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.createHinokCareGiftPolicy = factory;
})(globalThis, function createCareGiftPolicy(config) {
  const fullComponents = {
    'the-hand-set': ['the-hand-wash-450ml', 'the-hand-balm-35ml'],
    'the-shower-set': ['the-body-wash-15-2-fl-oz-450ml', 'the-shampoo-15-2-fl-oz-450ml', 'the-conditioner-15-2-fl-oz-450ml'],
    'the-spray-hand-wash-set': ['the-hand-wash-450ml']
  };
  const partialComponents = {
    'essential-to-go-kit': ['the-hand-balm-35ml'],
    'hair-body-discovery-set': ['the-body-wash-15-2-fl-oz-450ml']
  };
  function enabled() { return Boolean(config.enabled && config.tiered); }
  function tiers() { return enabled() ? config.tiers : []; }
  function eligible(total) { return tiers().filter(tier => tier.available && total >= tier.threshold); }
  function fulfilment(total) {
    const earned = eligible(total);
    if (earned.length === 2 && config.pairs?.length) {
      const pair = config.pairs.find(item => item.available);
      return pair ? [{ ...pair, key: 'care-pair', code: config.combinedCode }] : earned.slice(0, 1);
    }
    return earned;
  }
  function availableVariant(options) { return options.find(option => option.available)?.variant || 0; }
  function nextThreshold(total) { return tiers().find(tier => tier.available && total < tier.threshold)?.threshold || 0; }
  function progressThreshold(shipping) { return Math.max(shipping, ...tiers().filter(tier => tier.available).map(tier => tier.threshold)); }
  function managed(item) {
    const marker = item.properties && item.properties._hinok_gwp;
    return Boolean(marker && [config.marker, 'discovery-90-20261020', 'care-gift-20261006', 'to-go-70', 'hand-balm-80'].includes(marker));
  }
  function subtotal(cart) {
    return (cart.items || []).reduce((sum, item) => sum + (managed(item) || item.gift_card ? 0 : Number(item.final_line_price) || 0), 0);
  }
  function tierFor(item) {
    if (!item.properties || item.properties._hinok_gwp !== config.marker) return null;
    return [...config.tiers, ...(config.pairs || []).map(pair => ({ ...pair, key: 'care-pair' }))]
      .find(tier => tier.key === item.properties._hinok_gwp_tier && tier.variant === Number(item.variant_id)) || null;
  }
  function normalizeUpdates(cart, expected) {
    const kept = new Set();
    const updates = {};
    for (const item of (cart.items || []).filter(managed)) {
      const tier = tierFor(item);
      if (!tier || !expected.some(allowed => allowed.key === tier.key && allowed.variant === tier.variant) || kept.has(tier.key)) updates[item.key] = 0;
      else {
        kept.add(tier.key);
        if (item.quantity !== 1) updates[item.key] = 1;
      }
    }
    return updates;
  }
  function valid(cart, expected) {
    const gifts = (cart.items || []).filter(managed);
    return gifts.length === expected.length && expected.every(tier => {
      const matches = gifts.filter(item => tierFor(item)?.key === tier.key && Number(item.variant_id) === tier.variant);
      return matches.length === 1 && matches[0].quantity === 1 && matches[0].final_line_price === 0;
    });
  }
  function code(expected) {
    if (expected.length === 2) return config.combinedCode;
    return expected[0]?.code || '';
  }
  function expandHandles(handles) {
    return [...new Set(handles.flatMap(handle => [handle, ...(fullComponents[handle] || [])]))];
  }
  function paidOverlap(cart, handle) {
    return (cart.items || []).filter(item => !managed(item) && !item.gift_card).reduce((counts, item) => {
      const quantity = Number(item.quantity) || 0;
      if (item.handle === handle) counts.purchased += quantity;
      else if ((fullComponents[item.handle] || []).includes(handle)) counts.bundled += quantity;
      return counts;
    }, { purchased: 0, bundled: 0 });
  }
  function decision(candidate, total, cartHandles = []) {
    cartHandles = expandHandles(cartHandles);
    if (candidate.available === false || cartHandles.includes(candidate.handle)) return { blocked: true, penalty: 0 };
    if (!enabled()) return { blocked: false, penalty: 0 };
    const promised = eligible(total + Math.max(0, Number(candidate.price) || 0)).map(tier => tier.handle);
    const full = [candidate.handle, ...(fullComponents[candidate.handle] || [])];
    const blocked = full.some(handle => promised.includes(handle) || cartHandles.includes(handle));
    const partial = partialComponents[candidate.handle] || [];
    return { blocked, penalty: partial.some(handle => promised.includes(handle) || cartHandles.includes(handle)) ? 1 : 0 };
  }
  function rank(handles, candidates, total, cartHandles) {
    const target = nextThreshold(total);
    return [...new Set(handles)].map((handle, index) => {
      const candidate = candidates[handle];
      return candidate ? { handle, index, reachesTarget: Boolean(target && total + candidate.price >= target), ...decision(candidate, total, cartHandles) } : { handle, index, blocked: true };
    }).filter(item => !item.blocked).sort((a, b) => Number(b.reachesTarget) - Number(a.reachesTarget) || a.penalty - b.penalty || a.index - b.index).map(item => item.handle);
  }
  function format(cents) {
    return new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(cents / 100);
  }
  function copy(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, value), config.copy[key] || '');
  }
  function message(total, mode = 'included') {
    if (!enabled()) return '';
    const available = tiers().filter(tier => tier.available);
    if (!available.length) return copy('unavailable');
    if (mode === 'empty') return copy('empty', { amount: format(available[0].threshold), product: available[0].title });
    const earned = eligible(total);
    const next = available.find(tier => total < tier.threshold);
    const parts = [];
    if (earned.length) {
      const products = earned.map(tier => tier.title).join(copy('join'));
      const key = mode === 'added' && earned.length === tiers().length && config.copy.completed ? 'completed' : mode;
      parts.push(copy(key, { products }));
    }
    if (next) parts.push(copy('away', { amount: format(next.threshold - total), product: next.title }));
    tiers().filter(tier => !tier.available && total >= tier.threshold).forEach(tier => {
      parts.push(copy('gift_unavailable', { product: tier.title }));
    });
    return parts.join('\n');
  }
  return { enabled, tiers, eligible, fulfilment, availableVariant, nextThreshold, progressThreshold, managed, subtotal, tierFor, normalizeUpdates, valid, code, expandHandles, paidOverlap, decision, rank, copy, copyAmount: format, message };
});
